<template>
  <MatchingTaxonomiesDataProvider v-slot="{ taxonomies, loading: taxLoading }">
    <MatchingProfileDataProvider
      v-slot="{ profile, loading: profileLoading, saveProfile }"
    >
      <div
        :class="
          embedded
            ? 'w-full space-y-6'
            : 'p-6 md:p-8 max-w-large mx-auto w-full space-y-6'
        "
      >
        <!-- Header -->
        <div class="border-b border-slate-200 pb-5">
          <div class="flex items-start gap-3 min-w-0">
            <button
              v-if="!embedded"
              type="button"
              class="shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-sm sm:text-base rounded-xl border border-slate-200 text-slate-600 hover:text-teal-600 hover:border-teal-300 transition-colors"
              aria-label="Back to profile"
              title="Back to profile"
              @click="$router.push({ name: 'profile' }).catch(() => {})"
            >
              <i class="fal fa-arrow-left"></i>
            </button>
            <div class="min-w-0">
              <h1
                class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
              >
                {{ $t('Views.AiMatching.Profile.title', tLocale) }}
              </h1>
              <p class="text-sm text-slate-500 mt-1.5 leading-relaxed">
                {{ $t('Views.AiMatching.Profile.subtitle', tLocale) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div
          v-if="profileLoading || taxLoading"
          class="py-20 text-center bg-white border border-slate-200 rounded-3xl shadow-sm text-slate-400 font-medium"
        >
          <i
            class="fal fa-spinner-third animate-spin mr-2 text-teal-500 text-xl"
          ></i>
          <span>{{ $t('Views.AiMatching.Matches.loading', tLocale) }}</span>
        </div>

        <!-- Onboarding Form Card -->
        <form
          v-else
          class="bg-white rounded-3xl border border-slate-200 p-6 md:p-8 shadow-sm space-y-6"
          @submit.prevent="onSubmit(saveProfile)"
        >
          <!-- Core Details Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- Industry Select -->
            <FormField
              :label="$t('Views.AiMatching.Profile.industryLabel', tLocale)"
              required
              class="block"
            >
              <select
                v-model="form.industryId"
                class="form-select w-full border border-slate-200 rounded-xl px-4 py-3 bg-white text-slate-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all duration-200 text-sm shadow-sm"
                :class="{
                  'border-red-500 ring-2 ring-red-100': showError('industryId')
                }"
              >
                <option value="">
                  {{
                    $t('Views.AiMatching.Profile.industryPlaceholder', tLocale)
                  }}
                </option>
                <option
                  v-for="opt in taxonomies.industries"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ opt.label }}
                </option>
              </select>
            </FormField>

            <!-- Decision Level -->
            <FormField
              :label="
                $t('Views.AiMatching.Profile.decisionLevelLabel', tLocale)
              "
              required
              class="block"
            >
              <select
                v-model="form.decisionLevelId"
                class="form-select w-full border border-slate-200 rounded-xl px-4 py-3 bg-white text-slate-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all duration-200 text-sm shadow-sm"
                :class="{
                  'border-red-500 ring-2 ring-red-100':
                    showError('decisionLevelId')
                }"
              >
                <option value="">
                  {{
                    $t(
                      'Views.AiMatching.Profile.decisionLevelPlaceholder',
                      tLocale
                    )
                  }}
                </option>
                <option
                  v-for="opt in taxonomies.decisionLevels"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ opt.label }}
                </option>
              </select>
            </FormField>

            <!-- Org Type -->
            <FormField
              :label="$t('Views.AiMatching.Profile.orgTypeLabel', tLocale)"
              class="block"
            >
              <select
                v-model="form.orgTypeId"
                class="form-select w-full border border-slate-200 rounded-xl px-4 py-3 bg-white text-slate-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all duration-200 text-sm shadow-sm"
              >
                <option value="">
                  {{
                    $t('Views.AiMatching.Profile.orgTypePlaceholder', tLocale)
                  }}
                </option>
                <option
                  v-for="opt in taxonomies.orgTypes"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ opt.label }}
                </option>
              </select>
            </FormField>

            <!-- Budget Bracket -->
            <FormField
              :label="
                $t('Views.AiMatching.Profile.budgetBracketLabel', tLocale)
              "
              class="block"
            >
              <select
                v-model="form.budgetBracketId"
                class="form-select w-full border border-slate-200 rounded-xl px-4 py-3 bg-white text-slate-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all duration-200 text-sm shadow-sm"
              >
                <option value="">
                  {{
                    $t(
                      'Views.AiMatching.Profile.budgetBracketPlaceholder',
                      tLocale
                    )
                  }}
                </option>
                <option
                  v-for="opt in taxonomies.budgetBrackets"
                  :key="opt.id"
                  :value="opt.id"
                >
                  {{ opt.label }}
                </option>
              </select>
            </FormField>
          </div>

          <!-- Interest Tags Selection -->
          <FormField
            :label="$t('Views.AiMatching.Profile.interestTagsLabel', tLocale)"
            :help="$t('Views.AiMatching.Profile.interestTagsHelp', tLocale)"
            required
            class="block"
          >
            <div
              class="flex flex-wrap gap-2.5 p-3.5 bg-slate-50 border border-slate-200 rounded-2xl"
              :class="{
                'border-red-500 ring-2 ring-red-100':
                  showError('interestTagIds')
              }"
            >
              <button
                v-for="tag in interestTagOptions(taxonomies)"
                :key="tag.id"
                type="button"
                class="px-4.5 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 select-none cursor-pointer focus:outline-none min-h-[44px] md:min-h-0 inline-flex items-center"
                :class="
                  isTagSelected(tag.id)
                    ? 'bg-teal-50 text-teal-700 border-teal-500 font-semibold'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-800'
                "
                @click="toggleTag(tag.id)"
              >
                {{ tag.label }}
              </button>
            </div>
          </FormField>

          <!-- Networking Goals -->
          <FormField
            :label="
              $t('Views.AiMatching.Profile.networkingGoalsLabel', tLocale)
            "
            :help="$t('Views.AiMatching.Profile.networkingGoalsHelp', tLocale)"
            class="block"
          >
            <div
              class="flex flex-wrap gap-2.5 p-3.5 bg-slate-50 border border-slate-200 rounded-2xl"
            >
              <button
                v-for="goal in taxonomies.networkingGoals"
                :key="goal.id"
                type="button"
                class="px-4.5 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 select-none cursor-pointer focus:outline-none min-h-[44px] md:min-h-0 inline-flex items-center"
                :class="
                  isGoalSelected(goal.id)
                    ? 'bg-teal-50 text-teal-700 border-teal-500 font-semibold'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-800'
                "
                @click="toggleGoal(goal.id)"
              >
                {{ goal.label }}
              </button>
            </div>
          </FormField>

          <!-- Solution Categories -->
          <FormField
            :label="
              $t('Views.AiMatching.Profile.solutionCategoriesLabel', tLocale)
            "
            :help="
              $t('Views.AiMatching.Profile.solutionCategoriesHelp', tLocale)
            "
            class="block"
          >
            <div
              class="flex flex-wrap gap-2.5 p-3.5 bg-slate-50 border border-slate-200 rounded-2xl"
            >
              <button
                v-for="tag in interestTagOptions(taxonomies)"
                :key="tag.id"
                type="button"
                class="px-4.5 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 select-none cursor-pointer focus:outline-none min-h-[44px] md:min-h-0 inline-flex items-center"
                :class="
                  isSolutionCategorySelected(tag.id)
                    ? 'bg-teal-50 text-teal-700 border-teal-500 font-semibold'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-800'
                "
                @click="toggleSolutionCategory(tag.id)"
              >
                {{ tag.label }}
              </button>
            </div>
          </FormField>

          <!-- Form Validation Error -->
          <div
            v-if="formError"
            class="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-xl font-medium flex items-center gap-2"
          >
            <i class="fal fa-exclamation-circle text-base"></i>
            <span>{{
              $t('Views.AiMatching.Profile.requiredError', tLocale)
            }}</span>
          </div>

          <!-- Action Buttons Footer -->
          <div
            class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-100 pt-5"
          >
            <button
              v-if="!embedded"
              type="button"
              @click="skipSetup"
              class="text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors min-h-[44px] sm:min-h-0 inline-flex items-center justify-center sm:justify-start"
            >
              Skip for now
            </button>
            <span v-else></span>
            <button
              type="submit"
              :disabled="saving"
              class="w-full sm:w-auto btn-brand transition-all font-bold text-sm px-6 py-3.5 rounded-xl shadow-md disabled:opacity-50 inline-flex items-center justify-center gap-2 min-h-[44px]"
            >
              <i v-if="saving" class="fal fa-spinner-third animate-spin"></i>
              <span>{{
                $t('Views.AiMatching.Profile.saveButton', tLocale)
              }}</span>
            </button>
          </div>
        </form>
      </div>
    </MatchingProfileDataProvider>
  </MatchingTaxonomiesDataProvider>
</template>

<script>
import FormField from '@/components/aiMatching/FormField.vue';

export default {
  name: 'MatchingProfileForm',
  components: { FormField },
  props: {
    // When true the form is rendered inline inside another page (the
    // profile page): the back button and "Skip for now" are hidden and
    // saving keeps the user in place (no redirect to the dashboard).
    embedded: {
      type: Boolean,
      default: false
    },
    // The AI Matcher section is English-only, but this form is also embedded in the profile
    // page, which follows the event language. The AI Matcher's own page passes true.
    english: { type: Boolean, default: false }
  },
  data() {
    return {
      form: {
        industryId: '',
        decisionLevelId: '',
        orgTypeId: '',
        budgetBracketId: '',
        interestTagIds: [],
        networkingGoalIds: [],
        solutionCategoryIds: [],
        bio: '',
        painPoints: ''
      },
      attemptedSubmit: false,
      saving: false,
      hydratedFromProfile: false
    };
  },
  computed: {
    tLocale() {
      return this.english ? 'en' : this.$i18n.locale;
    },
    profileFromStore() {
      return this.$store.getters['MatchingProfile/getProfile'];
    },
    formError() {
      if (!this.attemptedSubmit) return false;
      return (
        !this.form.industryId ||
        !this.form.decisionLevelId ||
        this.form.interestTagIds.length === 0
      );
    }
  },
  watch: {
    profileFromStore: {
      immediate: true,
      handler(profile) {
        if (!profile || this.hydratedFromProfile) return;
        this.form = {
          industryId: profile.industryId || '',
          decisionLevelId: profile.decisionLevelId || '',
          orgTypeId: profile.orgTypeId || '',
          budgetBracketId: profile.budgetBracketId || '',
          interestTagIds: [...(profile.interestTagIds || [])],
          networkingGoalIds: [...(profile.networkingGoalIds || [])],
          solutionCategoryIds: [...(profile.solutionCategoryIds || [])],
          bio: profile.bio || '',
          painPoints: profile.painPoints || ''
        };
        this.hydratedFromProfile = true;
      }
    }
  },
  methods: {
    // Interest tags for the Interests + Solutions pickers. Prefer the
    // event-curated list; fall back to the master catalog when the admin
    // hasn't curated an event-specific set (otherwise both pickers render
    // empty even though tags exist).
    interestTagOptions(taxonomies) {
      if (!taxonomies) return [];
      const eventTags = taxonomies.eventInterestTags || [];
      if (eventTags.length) return eventTags;
      return taxonomies.masterInterestTags || [];
    },
    isTagSelected(id) {
      return this.form.interestTagIds.includes(id);
    },
    toggleTag(id) {
      const idx = this.form.interestTagIds.indexOf(id);
      if (idx === -1) this.form.interestTagIds.push(id);
      else this.form.interestTagIds.splice(idx, 1);
    },
    isGoalSelected(id) {
      return this.form.networkingGoalIds.includes(id);
    },
    toggleGoal(id) {
      const idx = this.form.networkingGoalIds.indexOf(id);
      if (idx === -1) this.form.networkingGoalIds.push(id);
      else this.form.networkingGoalIds.splice(idx, 1);
    },
    isSolutionCategorySelected(id) {
      return this.form.solutionCategoryIds.includes(id);
    },
    toggleSolutionCategory(id) {
      const idx = this.form.solutionCategoryIds.indexOf(id);
      if (idx === -1) this.form.solutionCategoryIds.push(id);
      else this.form.solutionCategoryIds.splice(idx, 1);
    },
    showError(field) {
      if (!this.attemptedSubmit) return false;
      if (field === 'interestTagIds')
        return this.form.interestTagIds.length === 0;
      return !this.form[field];
    },
    skipSetup() {
      this.$router.push({ name: 'lobby' }).catch(() => {});
    },
    async onSubmit(saveProfile) {
      this.attemptedSubmit = true;
      if (this.formError) return;
      this.saving = true;
      try {
        await saveProfile(this.form);
        this.$notify({
          group: 'default',
          title: this.$t('Views.AiMatching.Profile.savedToast', this.tLocale)
        });
        // Standalone onboarding → advance to the dashboard. Embedded in
        // the profile page → stay put (it's an in-place settings edit).
        if (!this.embedded) {
          this.$router.push({ name: 'dashboard' });
        }
      } catch (e) {
        this.$notify({
          group: 'default',
          type: 'error',
          title: this.$t(
            'Views.AiMatching.Profile.saveFailedToast',
            this.tLocale
          )
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.px-4\.5 {
  padding-left: 1.125rem;
  padding-right: 1.125rem;
}
</style>
